import React, { useState } from 'react';
import { LogOutIcon, TrashIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useJournal } from '../context/JournalContext';
import { CURRENCIES, MARKETS, TIMEZONES } from '../lib/auth';
import { Button } from '../components/ui/Button';
import { Field, Input, inputBase } from '../components/ui/Field';
import { AuthError, AuthNotice } from '../components/auth/AuthShell';
import { cn } from '../lib/cn';
export function ProfilePage() {
  const {
    user,
    updateProfile,
    changePassword,
    signOut,
    deleteAccount
  } = useAuth();
  const {
    activeOperations,
    trashedOperations
  } = useJournal();
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [timezone, setTimezone] = useState(user?.timezone ?? TIMEZONES[0]);
  const [currency, setCurrency] = useState(user?.currency ?? CURRENCIES[0]);
  const [market, setMarket] = useState(user?.market ?? MARKETS[0]);
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl);
  const [profileError, setProfileError] = useState<string>();
  const [profileNotice, setProfileNotice] = useState<string>();
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string>();
  const [passwordNotice, setPasswordNotice] = useState<string>();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  if (!user) return null;
  const saveProfile = async (event: React.FormEvent) => {
    event.preventDefault();
    setProfileNotice(undefined);
    const result = await updateProfile({
      name: name.trim(),
      email,
      timezone,
      currency,
      market,
      avatarUrl
    });
    setProfileError(result.error);
    if (!result.error) setProfileNotice('Perfil actualizado.');
  };
  const savePassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setPasswordNotice(undefined);
    const result = await changePassword(currentPassword, password, confirmPassword);
    setPasswordError(result.error);
    if (result.ok) {
      setPasswordNotice('Contraseña actualizada.');
      setCurrentPassword('');
      setPassword('');
      setConfirmPassword('');
    }
  };
  const readAvatar = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => setAvatarUrl(String(reader.result));
    reader.readAsDataURL(file);
  };
  const confirmDeleteAccount = async () => {
    try {
      await deleteAccount();
    } catch (err) {
      setProfileError('No se pudo eliminar la cuenta. Inténtalo de nuevo.');
    }
  };
  return <div className="mx-auto max-w-3xl" data-id="element-776" data-mp="3l2l4168qg6nr">
      <header className="mb-5" data-id="element-777" data-mp="3l1jgi2aj5rp5">
        <h1 className="text-xl font-semibold tracking-tight" data-id="element-778" data-mp="3l4xg52rt7msw">Mi perfil</h1>
        <p className="mt-0.5 text-[13px] text-ink-soft" data-id="element-779" data-mp="3g49uyxq9i06l">
          Tus datos son privados de esta cuenta: {activeOperations.length} operaciones activas y {trashedOperations.length} en la papelera.
        </p>
      </header>

      <form onSubmit={saveProfile} className="rounded-2xl border border-line bg-white p-5" data-id="element-780" data-mp="20mwox3ax6dzj">
        <h2 className="text-[15px] font-semibold tracking-tight" data-id="element-781" data-mp="24kaxqtuoujr0">Datos de la cuenta</h2>
        <div className="mt-4 flex flex-col gap-3.5" data-id="element-782" data-mp="29kp8wkrl69t9">
          <AuthError message={profileError} data-id="element-783" data-mp="1cvramskmntrc" />
          <AuthNotice message={profileNotice} data-id="element-784" data-mp="1cx2gqz1jpdlm" />

          <div className="flex items-center gap-3" data-id="element-785" data-mp="1gwb85x944ze0">
            {avatarUrl ? <img src={avatarUrl} alt="Foto de perfil" className="h-14 w-14 rounded-full object-cover" data-id="element-786" data-mp="1lvw9gebr9jtw" /> : <div className="flex h-14 w-14 items-center justify-center rounded-full bg-ink text-sm font-semibold text-white" data-id="element-787" data-mp="1qmw57g951vut">
                {user.name.slice(0, 2).toUpperCase()}
              </div>}
            <div className="flex flex-col gap-1" data-id="element-788" data-mp="y5a26p9ks3ky">
              <input type="file" accept="image/*" onChange={event => {
              const file = event.target.files?.[0];
              if (file) readAvatar(file);
            }} className="text-[12.5px] text-ink-soft" data-id="element-789" data-mp="y3z0cuzox5wh" />
              {avatarUrl && <button type="button" onClick={() => setAvatarUrl(undefined)} className="self-start text-[12px] text-ink-soft hover:text-neg" data-id="element-790" data-mp="39n56i402y7dd">
                  Quitar foto
                </button>}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2" data-id="element-791" data-mp="3ejc150n0zwg0">
            <Field label="Nombre" data-id="element-792" data-mp="3sjwkpz0rlwak"><Input value={name} onChange={event => setName(event.target.value)} data-id="element-793" data-mp="3skg31gubump4" /></Field>
            <Field label="Correo" data-id="element-794" data-mp="3oi1mx4i1m11j"><Input type="email" value={email} onChange={event => setEmail(event.target.value)} data-id="element-795" data-mp="3ojmh2t0g3hyv" /></Field>
            <Field label="Zona horaria" data-id="element-796" data-mp="1y6x0ttpcqfxh">
              <select value={timezone} onChange={event => setTimezone(event.target.value)} className={cn(inputBase)} data-id="element-797" data-mp="1y7yk2lgiu8qa">
                {TIMEZONES.map(item => <option key={item} value={item} data-id="element-798" data-mp="1k1oc486wwvxx">{item}</option>)}
              </select>
            </Field>
            <Field label="Moneda" data-id="element-799" data-mp="1o0x3x7ie6jqu">
              <select value={currency} onChange={event => setCurrency(event.target.value)} className={cn(inputBase)} data-id="element-800" data-mp="1o4b9eomcr92i">
                {CURRENCIES.map(item => <option key={item} value={item} data-id="element-801" data-mp="1f93ip7rx4kux">{item}</option>)}
              </select>
            </Field>
            <Field label="Idioma" data-id="element-802" data-mp="10bkv48qz8dp2">
              <select value="es-ES" disabled className={cn(inputBase, 'text-ink-soft')} data-id="element-803" data-mp="10eqmxvqzptoh">
                <option value="es-ES" data-id="element-804" data-mp="15d0hq58sys9k">Español (es-ES)</option>
              </select>
            </Field>
            <Field label="Mercado principal" data-id="element-805" data-mp="rdsff3hcf415">
              <select value={market} onChange={event => setMarket(event.target.value)} className={cn(inputBase)} data-id="element-806" data-mp="waiwnu4vw5o5">
                {MARKETS.map(item => <option key={item} value={item} data-id="element-807" data-mp="2lqi9bl7pod8j">{item}</option>)}
              </select>
            </Field>
          </div>

          <Button type="submit" variant="primary" size="sm" className="self-start" data-id="element-808" data-mp="2qmz7774pq5yt">Guardar cambios</Button>
        </div>
      </form>

      <form onSubmit={savePassword} className="mt-4 rounded-2xl border border-line bg-white p-5" data-id="element-809" data-mp="2k8a82blkx2ga">
        <h2 className="text-[15px] font-semibold tracking-tight" data-id="element-810" data-mp="2f96zwiaayg43">Contraseña</h2>
        <div className="mt-4 flex flex-col gap-3.5" data-id="element-811" data-mp="2aam0gl2vk0gp">
          <AuthError message={passwordError} data-id="element-812" data-mp="2abxc172kacid" />
          <AuthNotice message={passwordNotice} data-id="element-813" data-mp="37ye868zk6k2j" />
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3" data-id="element-814" data-mp="37x2wzo3saa1e">
            <Field label="Contraseña actual" data-id="element-815" data-mp="32yrn5ddhnzj6"><Input type="password" value={currentPassword} onChange={event => setCurrentPassword(event.target.value)} data-id="element-816" data-mp="330ma0x8bm1pw" /></Field>
            <Field label="Nueva contraseña" data-id="element-817" data-mp="2y687mav8qubq"><Input type="password" value={password} onChange={event => setPassword(event.target.value)} data-id="element-818" data-mp="2t9zx5arznpaw" /></Field>
            <Field label="Confirmar" data-id="element-819" data-mp="3qp5j3ez72ij1"><Input type="password" value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} data-id="element-820" data-mp="3qqghez9vey2r" /></Field>
          </div>
          <Button type="submit" variant="secondary" size="sm" className="self-start" data-id="element-821" data-mp="3t9jwf4kq2w61">Actualizar contraseña</Button>
        </div>
      </form>

      <div className="mt-4 rounded-2xl border border-line bg-white p-5" data-id="element-822" data-mp="23s7u9mlfrlc">
        <h2 className="text-[15px] font-semibold tracking-tight" data-id="element-823" data-mp="71kbg0wlp25d">Sesión</h2>
        <p className="mt-1 text-[13px] text-ink-soft" data-id="element-824" data-mp="c16qmfvzosgt">Al cerrar sesión volverás a la pantalla de acceso.</p>
        <Button variant="secondary" size="sm" className="mt-3" onClick={signOut} data-id="element-825" data-mp="g31nn86w0jos">
          <LogOutIcon className="h-3.5 w-3.5" data-id="element-826" data-mp="g0xkisgtbopk" />Cerrar sesión
        </Button>
      </div>

      <div className="mt-4 rounded-2xl border border-neg-border bg-neg-soft/40 p-5" data-id="element-827" data-mp="kzs2js6yh98m">
        <h2 className="text-[15px] font-semibold tracking-tight text-neg" data-id="element-828" data-mp="ptd3nt3uijc1">Eliminar mi cuenta</h2>
        <p className="mt-1 text-[13px] leading-relaxed text-ink-soft" data-id="element-829" data-mp="uqwhirwmsu3g">
          Se eliminarán definitivamente tus operaciones, imágenes, notas, páginas de journal, etiquetas, papelera y preferencias. No se puede deshacer.
        </p>
        <Button variant="danger" size="sm" className="mt-3" onClick={() => setConfirmDelete(true)} data-id="element-830" data-mp="1p3b44cziqoef">
          <TrashIcon className="h-3.5 w-3.5" data-id="element-831" data-mp="1l1zkco3bsxe5" />Eliminar mi cuenta
        </Button>
      </div>

      {confirmDelete && <div className="fixed inset-0 z-[90] flex items-center justify-center bg-ink/30 p-5" role="dialog" aria-modal="true" data-id="element-832" data-mp="1z1qsi875vjk8">
          <div className="w-full max-w-md rounded-2xl border border-line bg-white p-5 shadow-pop" data-id="element-833" data-mp="1u2xe8dk1voj0">
            <h3 className="text-[15px] font-semibold tracking-tight" data-id="element-834" data-mp="16dctpq890j7m">Confirmar eliminación</h3>
            <p className="mt-2 text-[13px] leading-relaxed text-ink-soft" data-id="element-835" data-mp="11f1hj8uhdwm8">
              Esta acción eliminará definitivamente tu cuenta y todos sus datos. Escribe <span className="font-semibold text-ink" data-id="element-836" data-mp="1bbwevof0pqyx">ELIMINAR</span> para confirmar.
            </p>
            <Input className="mt-3" value={deleteText} onChange={event => setDeleteText(event.target.value)} placeholder="ELIMINAR" data-id="element-837" data-mp="nm26bj1qeokh" />
            <div className="mt-4 flex justify-end gap-2" data-id="element-838" data-mp="im7bx5iqzguo">
              <Button variant="ghost" size="sm" onClick={() => {
            setConfirmDelete(false);
            setDeleteText('');
          }} data-id="element-839" data-mp="2uakk2i3zybhi">Cancelar</Button>
              <Button variant="danger" size="sm" disabled={deleteText.trim().toUpperCase() !== 'ELIMINAR'} onClick={confirmDeleteAccount} data-id="element-840" data-mp="2z1khdoh12vkj">
                Eliminar definitivamente
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}